import { useState, useEffect, useRef, useMemo } from "react";
import { apiCall } from "./api";
import { db, collection, doc, onSnapshot } from "./firebase";
import { FONT_BODY, FONT_DISPLAY, ELLIPSIS } from "./theme";
import { useTitleFlash } from "./title-flash";
import { SpectatingBadge, leaveLobbyBody } from "./spectator";
import { notify } from "./notify";
import NotificationsToggle from "./notifications-toggle";

// ============================================================
// BLACK OWL - Draft Screen
// Turn-based player drafting with position filters
// ============================================================

// --- Owl icon ---
function OwlIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="60" cy="68" rx="38" ry="42" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2.5"/>
      <polygon points="30,30 38,48 22,45" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2"/>
      <polygon points="90,30 82,48 98,45" fill="#1a1a2e" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="44" cy="55" r="14" fill="#0d0d1a" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="76" cy="55" r="14" fill="#0d0d1a" stroke="#c9a227" strokeWidth="2"/>
      <circle cx="44" cy="55" r="8" fill="#c9a227"/>
      <circle cx="76" cy="55" r="8" fill="#c9a227"/>
      <circle cx="44" cy="55" r="4" fill="#0d0d1a"/>
      <circle cx="76" cy="55" r="4" fill="#0d0d1a"/>
      <circle cx="47" cy="52" r="2" fill="#fff" opacity="0.7"/>
      <circle cx="79" cy="52" r="2" fill="#fff" opacity="0.7"/>
      <polygon points="55,65 60,75 65,65" fill="#c9a227"/>
    </svg>
  );
}

// --- Background ---
function FieldBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: "linear-gradient(165deg, #0a0a14 0%, #0f1a0f 40%, #0a0a14 100%)",
    }}>
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute", left: 0, right: 0,
          top: `${(i + 1) * 8}%`, height: "1px",
          background: `rgba(201, 162, 39, ${i === 5 ? 0.12 : 0.04})`,
        }}/>
      ))}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
      }}/>
    </div>
  );
}

// --- Position badge colors ---
const positionColors = {
  QB: { bg: "rgba(220, 80, 80, 0.15)", border: "rgba(220, 80, 80, 0.35)", text: "#dc5050" },
  RB: { bg: "rgba(74, 158, 255, 0.15)", border: "rgba(74, 158, 255, 0.35)", text: "#4a9eff" },
  WR: { bg: "rgba(50, 200, 120, 0.15)", border: "rgba(50, 200, 120, 0.35)", text: "#32c878" },
  TE: { bg: "rgba(200, 160, 50, 0.15)", border: "rgba(200, 160, 50, 0.35)", text: "#c8a032" },
  K: { bg: "rgba(180, 100, 220, 0.15)", border: "rgba(180, 100, 220, 0.35)", text: "#b464dc" },
  DEF: { bg: "rgba(140, 140, 160, 0.15)", border: "rgba(140, 140, 160, 0.35)", text: "#8c8ca0" },
};

function getPositionColor(pos) {
  return positionColors[pos] || positionColors["DEF"];
}

// --- Position filter pill ---
function PositionPill({ label, active, onClick, count }) {
  const c = label === "ALL"
    ? { bg: "rgba(201, 162, 39, 0.15)", border: "rgba(201, 162, 39, 0.35)", text: "#c9a227" }
    : getPositionColor(label);
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 14px",
        fontSize: "11px", fontWeight: 700, letterSpacing: "1.5px",
        fontFamily: FONT_BODY,
        color: active ? "#0a0a14" : c.text,
        background: active ? c.text : c.bg,
        border: `1.5px solid ${active ? c.text : c.border}`,
        borderRadius: "20px", cursor: "pointer",
        transition: "all 0.25s ease",
        display: "inline-flex", alignItems: "center", gap: "6px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
      {count !== undefined && (
        <span style={{
          fontSize: "10px", opacity: 0.7,
        }}>({count})</span>
      )}
    </button>
  );
}

// --- Player row (available to draft) ---
function PlayerRow({ player, onDraft, isMyTurn, drafting }) {
  const c = getPositionColor(player.position);
  return (
    <div style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "12px 16px",
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.05)",
      borderRadius: "10px", marginBottom: "6px",
      transition: "all 0.2s ease",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flex: 1 }}>
        <span style={{
          display: "inline-block", padding: "4px 8px",
          fontSize: "10px", fontWeight: 700, letterSpacing: "1px",
          fontFamily: FONT_BODY,
          color: c.text, background: c.bg,
          border: `1px solid ${c.border}`, borderRadius: "4px",
          minWidth: "32px", textAlign: "center",
        }}>{player.position}</span>
        <div style={{ flex: 1 }}>
          <p style={{
            fontSize: "14px", fontWeight: 600, color: "#e8e6df",
            margin: 0, fontFamily: FONT_BODY,
          }}>{player.name}</p>
          <p style={{
            fontSize: "11px", color: "#5a5a6e", margin: "2px 0 0",
            fontFamily: FONT_BODY,
          }}>{player.team}</p>
        </div>
        {typeof player.copies === "number" && (
          <span style={{
            fontSize: "10px", fontWeight: 700, letterSpacing: "1px",
            fontFamily: FONT_BODY,
            color: player.copies === 0 ? "#e85555" : "#c9a227",
            padding: "3px 8px",
            background: player.copies === 0 ? "rgba(220,50,50,0.1)" : "rgba(201,162,39,0.1)",
            border: `1px solid ${player.copies === 0 ? "rgba(220,50,50,0.25)" : "rgba(201,162,39,0.25)"}`,
            borderRadius: "10px",
            whiteSpace: "nowrap",
          }}>
            {player.copies + 1} left
          </span>
        )}
      </div>
      {isMyTurn && (
        <button
          onClick={() => onDraft(player.player_id)}
          disabled={drafting}
          style={{
            padding: "8px 16px", fontSize: "11px",
            fontWeight: 700, letterSpacing: "1.5px",
            textTransform: "uppercase",
            fontFamily: FONT_BODY,
            color: "#0a0a14",
            background: drafting ? "rgba(201, 162, 39, 0.5)" : "#c9a227",
            border: "none", borderRadius: "6px",
            cursor: drafting ? "wait" : "pointer",
            transition: "all 0.2s ease",
            flexShrink: 0,
          }}
        >
          Draft
        </button>
      )}
    </div>
  );
}

// --- Drafted player (in team roster) ---
function DraftedPlayer({ pick }) {
  const c = getPositionColor(pick.position);
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: "10px",
      padding: "10px 14px",
      background: "rgba(201, 162, 39, 0.04)",
      border: "1px solid rgba(201, 162, 39, 0.1)",
      borderRadius: "8px", marginBottom: "6px",
    }}>
      <span style={{
        display: "inline-block", padding: "3px 6px",
        fontSize: "9px", fontWeight: 700, letterSpacing: "1px",
        fontFamily: FONT_BODY,
        color: c.text, background: c.bg,
        border: `1px solid ${c.border}`, borderRadius: "3px",
      }}>{pick.position}</span>
      <div style={{ flex: 1 }}>
        <p style={{
          fontSize: "13px", fontWeight: 600, color: "#e8e6df",
          margin: 0, fontFamily: FONT_BODY,
        }}>{pick.name}</p>
      </div>
      <span style={{
        fontSize: "10px", color: "#5a5a6e",
        fontFamily: FONT_BODY,
      }}>Rd {pick.round}</span>
    </div>
  );
}

// --- Positional limit ticker (FB-11) ---
const DRAFT_POSITIONS = ["QB", "RB", "WR", "TE", "K"];

function PositionTicker({ filled, limits }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", gap: "6px",
      padding: "10px 14px", marginBottom: "12px",
      background: "rgba(12, 12, 22, 0.9)",
      border: "1px solid rgba(201, 162, 39, 0.12)",
      borderRadius: "10px",
    }}>
      {DRAFT_POSITIONS.map((pos) => {
        const c = getPositionColor(pos);
        const done = filled[pos] >= limits[pos];
        return (
          <span key={pos} style={{
            fontSize: "12px", fontWeight: 700, letterSpacing: "1px",
            fontFamily: FONT_BODY, fontVariantNumeric: "tabular-nums",
            color: done ? "#3a3a4e" : c.text,
            textDecoration: done ? "line-through" : "none",
          }}>
            {pos} {filled[pos]}/{limits[pos]}
          </span>
        );
      })}
    </div>
  );
}

// --- Another user's picks so far (FB-03) ---
function TeamCard({ team, isYou }) {
  return (
    <div style={{
      padding: "12px 14px", marginBottom: "10px",
      background: isYou ? "rgba(201, 162, 39, 0.04)" : "rgba(255,255,255,0.02)",
      border: `1px solid ${isYou ? "rgba(201, 162, 39, 0.12)" : "rgba(255,255,255,0.05)"}`,
      borderRadius: "10px",
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: "6px", margin: "0 0 2px" }}>
        <p style={{
          fontSize: "14px", fontWeight: 700, color: "#e8e6df",
          margin: 0, fontFamily: FONT_BODY, ...ELLIPSIS,
        }}>{team.teamName}</p>
        {isYou && (
          <span style={{ fontSize: "12px", color: "#c9a227", fontFamily: FONT_BODY, flexShrink: 0 }}>(You)</span>
        )}
      </div>
      <p style={{
        fontSize: "11px", color: "#5a5a6e", margin: "0 0 10px",
        fontFamily: FONT_BODY, ...ELLIPSIS,
      }}>
        {team.username !== team.teamName && `${team.username} • `}
        {team.picks.length} pick{team.picks.length !== 1 ? "s" : ""}
      </p>
      {team.picks.length === 0 ? (
        <p style={{ fontSize: "12px", color: "#3a3a4e", margin: 0, fontFamily: FONT_BODY }}>
          No picks yet
        </p>
      ) : (
        team.picks.map((pick) => (
          <DraftedPlayer key={pick.player_id} pick={{ ...pick, round: pick.draftedAtRound }} />
        ))
      )}
    </div>
  );
}

// --- FB-22: this round's pick order, who's on the clock, and where you sit.
// draft_order is the full snake sequence (one entry per pick), so the
// current round is a slice of it and even rounds run in reverse. ---
function PickOrderStrip({ draftOrder, draftIndex, username, teamNames, isSpectator }) {
  const teamCount = new Set(draftOrder).size;
  if (!teamCount || draftIndex >= draftOrder.length) return null;
  const roundIdx = Math.floor(draftIndex / teamCount);
  const roundStart = roundIdx * teamCount;
  const roundOrder = draftOrder.slice(roundStart, roundStart + teamCount);
  const nameOf = (u) => teamNames[u] || u;
  const onClock = draftOrder[draftIndex];
  const next = draftOrder[draftIndex + 1];
  const myNext = draftOrder.indexOf(username, draftIndex);
  const picksAway = myNext - draftIndex;
  const mySlot = roundOrder.indexOf(username) + 1;

  let status;
  if (isSpectator) status = `${nameOf(onClock)} is on the clock`;
  else if (onClock === username) status = "You're up!";
  else if (myNext === -1) status = "You're done picking";
  else status = `${mySlot > 0 ? `You're pick #${mySlot} this round · ` : ""}up in ${picksAway} pick${picksAway !== 1 ? "s" : ""}`;

  return (
    <div style={{ marginBottom: "10px" }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        gap: "8px", marginBottom: "6px",
      }}>
        <span style={{
          fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
          textTransform: "uppercase", color: "#5a5a6e", fontFamily: FONT_BODY, flexShrink: 0,
        }}>
          Round {roundIdx + 1} {teamCount > 1 && (roundIdx % 2 === 0 ? "→" : "← snake")}
        </span>
        <span style={{
          fontSize: "12px", fontWeight: 700, fontFamily: FONT_BODY, ...ELLIPSIS,
          color: onClock === username ? "#c9a227" : "#a0a0b0",
        }}>{status}</span>
      </div>
      <div style={{ display: "flex", gap: "4px" }}>
        {roundOrder.map((u, i) => {
          const pickIdx = roundStart + i;
          const current = pickIdx === draftIndex;
          const done = pickIdx < draftIndex;
          const you = u === username;
          return (
            <div key={`${u}-${i}`} style={{
              flex: "1 1 0", minWidth: 0,
              padding: "6px 6px",
              borderRadius: "8px", textAlign: "center",
              background: current ? "rgba(201, 162, 39, 0.18)" : "rgba(255,255,255,0.03)",
              border: `1.5px solid ${current ? "#c9a227" : you ? "rgba(201, 162, 39, 0.3)" : "rgba(255,255,255,0.06)"}`,
              opacity: done ? 0.4 : 1,
            }}>
              <div style={{
                fontSize: "10px", fontWeight: 700, fontFamily: FONT_BODY,
                color: current ? "#c9a227" : "#5a5a6e",
              }}>{done ? "✓" : `#${i + 1}`}</div>
              <div style={{
                fontSize: "11px", fontWeight: current || you ? 700 : 500, fontFamily: FONT_BODY,
                color: current ? "#e8e6df" : you ? "#c9a227" : "#a0a0b0", ...ELLIPSIS,
              }}>{you ? "You" : nameOf(u)}</div>
            </div>
          );
        })}
      </div>
      {next && onClock !== username && (
        <p style={{
          fontSize: "11px", color: "#5a5a6e", margin: "6px 0 0", fontFamily: FONT_BODY, ...ELLIPSIS,
        }}>Next: {next === username ? "You" : nameOf(next)}</p>
      )}
    </div>
  );
}

// --- Turn indicator banner (your turn is gold, FB-22) ---
function TurnBanner({ currentTurn, username, round, totalPicks, currentPick, teamNames }) {
  const isMyTurn = currentTurn === username;
  return (
    <div style={{
      padding: "16px 20px",
      background: isMyTurn ? "rgba(201, 162, 39, 0.16)" : "rgba(255,255,255,0.02)",
      border: `${isMyTurn ? "2px" : "1.5px"} solid ${isMyTurn ? "#c9a227" : "rgba(255,255,255,0.06)"}`,
      boxShadow: isMyTurn ? "0 0 24px rgba(201, 162, 39, 0.25)" : "none",
      borderRadius: "12px",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      gap: "12px", marginBottom: "16px",
    }}>
      <div style={{ minWidth: 0 }}>
        <p style={{
          fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
          textTransform: "uppercase", margin: "0 0 4px",
          fontFamily: FONT_BODY,
          color: isMyTurn ? "#c9a227" : "#5a5a6e",
        }}>
          {isMyTurn ? "You're up!" : "On the Clock"}
        </p>
        <p style={{
          fontSize: isMyTurn ? "20px" : "18px", fontWeight: 700, margin: 0,
          color: isMyTurn ? "#e8e6df" : "#c9a227",
          fontFamily: FONT_BODY, ...ELLIPSIS,
        }}>
          {isMyTurn ? "Make your pick" : (teamNames[currentTurn] || currentTurn)}
        </p>
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <p style={{
          fontSize: "10px", fontWeight: 600, letterSpacing: "2px",
          textTransform: "uppercase", margin: "0 0 4px",
          fontFamily: FONT_BODY, color: "#5a5a6e",
        }}>Round {round}</p>
        <p style={{
          fontSize: "14px", fontWeight: 600, margin: 0,
          color: "#7a7a8e", fontFamily: FONT_BODY,
        }}>Pick {currentPick}/{totalPicks}</p>
      </div>
    </div>
  );
}

// --- Tab button ---
function TabButton({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: "10px",
        fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
        textTransform: "uppercase",
        fontFamily: FONT_BODY,
        color: active ? "#0a0a14" : "#5a5a6e",
        background: active ? "#c9a227" : "transparent",
        border: "none", borderRadius: "6px",
        cursor: "pointer", transition: "all 0.3s ease",
      }}
    >{label}</button>
  );
}

// --- Search input ---
function SearchInput({ value, onChange }) {
  return (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder="Search players..."
      style={{
        width: "100%", padding: "10px 14px",
        fontSize: "13px", fontFamily: FONT_BODY,
        color: "#e8e6df", background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: "8px", outline: "none",
        marginBottom: "12px", boxSizing: "border-box",
      }}
    />
  );
}

// --- Main Draft Screen ---
export default function DraftScreen({ username, lobbyId, isSpectator, onDraftComplete, onLeaveLobby }) {
  const [tab, setTab] = useState("players"); // "players" or "myTeam" or "allPicks"
  const [positionFilter, setPositionFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [myPicks, setMyPicks] = useState([]);
  const [allPicks, setAllPicks] = useState([]);
  const [teams, setTeams] = useState([]);
  const [openSlots, setOpenSlots] = useState({});
  const [positionLimits, setPositionLimits] = useState({ QB: 1, RB: 1, WR: 1, TE: 1, K: 1 });
  const [draftState, setDraftState] = useState({
    currentTurn: username,
    round: 1,
    draftIndex: 0,
    totalPicks: 16,
    draftOrder: [],
  });
  const [loading, setLoading] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef(null);

  const isMyTurn = !isSpectator && draftState.currentTurn === username && draftState.draftIndex < draftState.totalPicks;
  const draftComplete = draftState.draftIndex >= draftState.totalPicks && draftState.totalPicks > 0;
  const teamNames = useMemo(
    () => Object.fromEntries(teams.map((t) => [t.username, t.teamName])),
    [teams]
  );
  // FB-22: flash the tab title while it's your pick
  useTitleFlash(isMyTurn ? "Your pick! · Black Owl" : "");
  // FB-15: and a browser notification if the tab is in the background
  useEffect(() => {
    if (isMyTurn) notify("Your pick!", "You're on the clock in the draft.", "draft-turn");
  }, [isMyTurn]);

  // Real-time listeners for draft state
  useEffect(() => {
    setLoading(true);

    // Listen for lobby draft state (whose turn, round, etc.)
    const lobbyRef = doc(db, "Lobbies", lobbyId);
    const unsubLobby = onSnapshot(lobbyRef, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        setDraftState({
          currentTurn: data.current_draft_turn || "",
          round: data.round || 1,
          draftIndex: data.draft_index || 0,
          totalPicks: data.total_draft_picks || 16,
          draftOrder: data.draft_order || [],
        });
        setPositionLimits({
          QB: data.number_of_qbs ?? 1,
          RB: data.number_of_rbs ?? 1,
          WR: data.number_of_wrs ?? 1,
          TE: data.number_of_tes ?? 1,
          K: data.number_of_ks ?? 1,
        });
        // If draft is complete (no more turns), navigate to game
        if (data.ready_to_play && onDraftComplete) {
          onDraftComplete();
        }
      }
    });

    // Listen for available players (match_roster)
    const rosterRef = collection(db, "Lobbies", lobbyId, "match_roster");
    const unsubRoster = onSnapshot(rosterRef, (snapshot) => {
      const players = snapshot.docs
        .map((doc) => ({ ...doc.data(), player_id: doc.id }))
        .filter((p) => p.copies >= 0); // -1 means gone; 0 or higher is still draftable
      setAvailablePlayers(players);
      setLoading(false);
    });

    // Listen for picks history
    const picksRef = collection(db, "Lobbies", lobbyId, "picks");
    const unsubPicks = onSnapshot(picksRef, (snapshot) => {
      const picks = snapshot.docs.map((doc) => ({
        ...doc.data(),
        pickNumber: parseInt(doc.id),
      })).sort((a, b) => a.pickNumber - b.pickNumber);
      setAllPicks(picks);
    });

    // Listen for my team's roster
    const myPlayerRef = doc(db, "Lobbies", lobbyId, "players", username);
    const unsubMyPlayer = onSnapshot(myPlayerRef, (snapshot) => {
      const data = snapshot.data();
      if (data) {
        // <POS>_amount counts open slots (1 = still open, 0 = filled)
        const slots = {};
        DRAFT_POSITIONS.forEach((pos) => {
          if (typeof data[`${pos}_amount`] === "number") slots[pos] = data[`${pos}_amount`];
        });
        setOpenSlots(slots);
      }
      if (data && data.roster) {
        const picks = Object.entries(data.roster).map(([playerId, info]) => ({
          player_id: playerId,
          ...info,
        }));
        setMyPicks(picks);
      }
    });

    // Listen for every user's roster (Teams tab, and names for History)
    const playersRef = collection(db, "Lobbies", lobbyId, "players");
    const unsubPlayers = onSnapshot(playersRef, (snapshot) => {
      setTeams(snapshot.docs.map((d) => {
        const data = d.data();
        const picks = Object.entries(data.roster || {})
          .map(([playerId, info]) => ({ player_id: playerId, ...info }))
          .sort((a, b) => (a.draftedAtRound || 0) - (b.draftedAtRound || 0));
        return { username: d.id, teamName: data.teamName || d.id, picks };
      }));
    });

    return () => {
      unsubLobby();
      unsubRoster();
      unsubPicks();
      unsubMyPlayer();
      unsubPlayers();
    };
  }, [lobbyId]);

  const handleDraft = async (playerId) => {
    setDrafting(true);
    setError("");
    try {
      const result = await apiCall("make_pick", {
        lobby_id: lobbyId,
        username: username,
        playerId: playerId,
      });
      if (!result.success) {
        setError(result.error || "Pick failed");
      }
      // No need to update state manually — Firestore listeners will
      // automatically update availablePlayers, myPicks, allPicks, and draftState
    } catch (err) {
      setError(err.message || "Failed to make pick");
    } finally {
      setDrafting(false);
    }
  };

  // Positions this user has filled are hidden from the filters and from ALL (D6)
  const isMaxed = (pos) => openSlots[pos] !== undefined && openSlots[pos] <= 0;
  const filledCounts = {};
  DRAFT_POSITIONS.forEach((pos) => {
    filledCounts[pos] = Math.max(positionLimits[pos] - (openSlots[pos] ?? positionLimits[pos]), 0);
  });
  const draftablePlayers = availablePlayers.filter((p) => !isMaxed(p.position));

  // Name/position lookup for pick history (pick docs only store the player ID)
  const pickedInfo = {};
  teams.forEach((t) => t.picks.forEach((p) => { pickedInfo[p.player_id] = p; }));

  // If the selected position just filled up, fall back to ALL
  const positions = ["ALL", ...DRAFT_POSITIONS.filter((pos) => !isMaxed(pos))];
  const activeFilter = positions.includes(positionFilter) ? positionFilter : "ALL";

  // Filter logic
  const filteredPlayers = draftablePlayers.filter((p) => {
    const matchesPosition = activeFilter === "ALL" || p.position === activeFilter;
    const matchesSearch = searchQuery === "" ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.team.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesPosition && matchesSearch;
  });

  // Count by position
  const positionCounts = {};
  availablePlayers.forEach((p) => {
    positionCounts[p.position] = (positionCounts[p.position] || 0) + 1;
  });

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex", alignItems: "flex-start", justifyContent: "center",
      fontFamily: FONT_BODY, position: "relative",
      paddingTop: "30px", paddingBottom: "40px",
    }}>
      <FieldBackground />

      <div style={{
        position: "relative", zIndex: 1,
        width: "100%", maxWidth: "500px", padding: "0 16px",
      }}>
        {/* Header */}
        <div style={{
          background: "rgba(12, 12, 22, 0.9)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "14px",
          padding: "18px 22px",
          boxShadow: "0 16px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
          marginBottom: "12px",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <OwlIcon />
              <div>
                <h1 style={{
                  fontSize: "15px", fontWeight: 700, color: "#c9a227",
                  margin: 0, letterSpacing: "2px", textTransform: "uppercase",
                  fontFamily: FONT_DISPLAY,
                }}>Draft Room</h1>
                <p style={{
                  fontSize: "11px", color: "#5a5a6e", margin: "2px 0 0",
                  fontFamily: FONT_BODY,
                }}>Lobby {lobbyId}</p>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {isSpectator ? <SpectatingBadge /> : (
                <div style={{
                  padding: "6px 12px",
                  background: isMyTurn ? "rgba(50, 200, 80, 0.12)" : "rgba(255,255,255,0.04)",
                  border: `1px solid ${isMyTurn ? "rgba(50, 200, 80, 0.3)" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: "20px",
                }}>
                  <span style={{
                    fontSize: "10px", fontWeight: 700, letterSpacing: "1.5px",
                    textTransform: "uppercase",
                    fontFamily: FONT_BODY,
                    color: isMyTurn ? "#32c850" : "#5a5a6e",
                  }}>
                    {isMyTurn ? "⚡ Your Turn" : "Waiting..."}
                  </span>
                </div>
              )}
              <button
                onClick={async () => {
                  if (isSpectator || window.confirm("Leave this lobby? This cannot be undone.")) {
                    try {
                      await apiCall("leave_lobby", leaveLobbyBody(username, lobbyId, isSpectator));
                    } catch (err) {}
                    if (onLeaveLobby) onLeaveLobby();
                  }
                }}
                style={{
                  padding: "6px 12px", fontSize: "10px",
                  fontWeight: 600, letterSpacing: "1.5px",
                  textTransform: "uppercase",
                  fontFamily: FONT_BODY,
                  color: "#e85555", background: "rgba(220, 50, 50, 0.08)",
                  border: "1px solid rgba(220, 50, 50, 0.2)",
                  borderRadius: "6px", cursor: "pointer",
                }}
              >Leave</button>
            </div>
          </div>
        </div>

        {/* Matchups display */}
        {(() => {
          const teams = new Set();
          availablePlayers.forEach(p => p.team && teams.add(p.team));
          myPicks.forEach(p => p.team && teams.add(p.team));
          allPicks.forEach(p => p.team && teams.add(p.team));
          const teamList = Array.from(teams);
          if (teamList.length === 0) return null;
          return (
            <div style={{
              padding: "10px 16px",
              background: "rgba(74, 158, 255, 0.06)",
              border: "1px solid rgba(74, 158, 255, 0.15)",
              borderRadius: "10px",
              marginBottom: "12px",
            }}>
              <p style={{
                fontSize: "10px", color: "#4a9eff",
                margin: "0 0 4px", fontFamily: FONT_BODY,
                letterSpacing: "1.5px", textTransform: "uppercase",
                fontWeight: 700,
              }}>Matchups</p>
              <p style={{
                fontSize: "12px", color: "#e8e6df", margin: 0,
                fontFamily: FONT_BODY,
              }}>
                {teamList.join(" · ")}
              </p>
            </div>
          );
        })()}

        {/* Draft Complete */}
        {draftComplete && (
          <div style={{
            padding: "20px 24px",
            background: "rgba(50, 200, 80, 0.08)",
            border: "1.5px solid rgba(50, 200, 80, 0.3)",
            borderRadius: "12px",
            textAlign: "center",
            marginBottom: "12px",
          }}>
            <p style={{
              fontSize: "18px", fontWeight: 700, color: "#32c850",
              margin: "0 0 8px", fontFamily: FONT_BODY,
            }}>Draft Complete!</p>
            <p style={{
              fontSize: "13px", color: "#5a5a6e", margin: "0 0 16px",
              fontFamily: FONT_BODY,
            }}>{isSpectator ? "All picks are in. Waiting for a player to start the game." : "All picks are in. Ready to start the game."}</p>
            {!isSpectator && <button
              onClick={async () => {
                try {
                  await apiCall("begin_game", {
                    username: username,
                    lobby_id: lobbyId,
                    interval: 15,
                  });
                } catch (err) {
                  setError(err.message);
                }
              }}
              style={{
                padding: "14px 32px",
                fontSize: "14px", fontWeight: 700,
                letterSpacing: "3px", textTransform: "uppercase",
                fontFamily: FONT_BODY,
                color: "#0a0a14",
                background: "linear-gradient(135deg, #32c850 0%, #4ade6a 50%, #32c850 100%)",
                border: "none", borderRadius: "8px",
                cursor: "pointer",
                boxShadow: "0 4px 20px rgba(50, 200, 80, 0.3)",
              }}
            >
              Start Game
            </button>}
          </div>
        )}

        {/* Pick order + turn banner (only during active draft) */}
        {!draftComplete && (
          <>
            <PickOrderStrip
              draftOrder={draftState.draftOrder}
              draftIndex={draftState.draftIndex}
              username={username}
              teamNames={teamNames}
              isSpectator={isSpectator}
            />
            <TurnBanner
              currentTurn={draftState.currentTurn}
              username={username}
              round={draftState.round}
              totalPicks={draftState.totalPicks}
              currentPick={Math.min(draftState.draftIndex + 1, draftState.totalPicks)}
              teamNames={teamNames}
            />
          </>
        )}

        {/* Positional limits */}
        {!draftComplete && <PositionTicker filled={filledCounts} limits={positionLimits} />}

        {/* Main content card */}
        <div style={{
          background: "rgba(12, 12, 22, 0.9)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "14px",
          padding: "20px",
          boxShadow: "0 16px 60px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
          marginBottom: "12px",
        }}>
          {/* Tabs */}
          <div style={{
            display: "flex", gap: "4px",
            marginBottom: "16px",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "8px", padding: "3px",
            border: "1px solid rgba(255,255,255,0.05)",
          }}>
            <TabButton label="Players" active={tab === "players"} onClick={() => setTab("players")} />
            {!isSpectator && (
              <TabButton label={`My Team (${myPicks.length})`} active={tab === "myTeam"} onClick={() => setTab("myTeam")} />
            )}
            <TabButton label="Teams" active={tab === "teams"} onClick={() => setTab("teams")} />
            <TabButton label="History" active={tab === "allPicks"} onClick={() => setTab("allPicks")} />
          </div>

          {/* --- Players Tab --- */}
          {tab === "players" && (
            <>
              <SearchInput value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />

              {/* Position filters */}
              <div style={{
                display: "flex", gap: "6px", marginBottom: "16px",
                overflowX: "auto", paddingBottom: "4px",
              }}>
                {positions.map((pos) => (
                  <PositionPill
                    key={pos}
                    label={pos}
                    active={activeFilter === pos}
                    onClick={() => setPositionFilter(pos)}
                    count={pos === "ALL" ? draftablePlayers.length : positionCounts[pos]}
                  />
                ))}
              </div>

              {/* Player list */}
              <div ref={listRef} style={{
                maxHeight: "400px", overflowY: "auto",
                paddingRight: "4px",
              }}>
                {loading ? (
                  <div style={{
                    textAlign: "center", padding: "40px 0",
                    color: "#5a5a6e", fontSize: "13px",
                    fontFamily: FONT_BODY,
                  }}>Loading players...</div>
                ) : filteredPlayers.length === 0 ? (
                  <div style={{
                    textAlign: "center", padding: "40px 0",
                    color: "#5a5a6e", fontSize: "13px",
                    fontFamily: FONT_BODY,
                  }}>No players match your filters.</div>
                ) : (
                  filteredPlayers.map((player) => (
                    <PlayerRow
                      key={player.player_id}
                      player={player}
                      onDraft={handleDraft}
                      isMyTurn={isMyTurn}
                      drafting={drafting}
                    />
                  ))
                )}
              </div>
            </>
          )}

          {/* --- My Team Tab --- */}
          {tab === "myTeam" && (
            <>
              {myPicks.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "40px 0",
                  color: "#5a5a6e", fontSize: "13px",
                  fontFamily: FONT_BODY,
                }}>No picks yet. Draft some players!</div>
              ) : (
                <>
                  {/* Group by position */}
                  {["QB", "RB", "WR", "TE", "K"].map((pos) => {
                    const picks = myPicks.filter((p) => p.position === pos);
                    if (picks.length === 0) return null;
                    const c = getPositionColor(pos);
                    return (
                      <div key={pos} style={{ marginBottom: "16px" }}>
                        <p style={{
                          fontSize: "11px", fontWeight: 700, letterSpacing: "2px",
                          textTransform: "uppercase", color: c.text,
                          margin: "0 0 8px", fontFamily: FONT_BODY,
                        }}>{pos}</p>
                        {picks.map((pick, i) => (
                          <DraftedPlayer key={i} pick={pick} />
                        ))}
                      </div>
                    );
                  })}

                  {/* Team summary */}
                  <div style={{
                    marginTop: "16px", padding: "14px 16px",
                    background: "rgba(201, 162, 39, 0.04)",
                    border: "1px solid rgba(201, 162, 39, 0.12)",
                    borderRadius: "10px",
                    display: "flex", justifyContent: "space-around",
                  }}>
                    {["QB", "RB", "WR", "TE", "K"].map((pos) => {
                      const count = myPicks.filter((p) => p.position === pos).length;
                      const c = getPositionColor(pos);
                      return (
                        <div key={pos} style={{ textAlign: "center" }}>
                          <p style={{
                            fontSize: "18px", fontWeight: 700, color: c.text,
                            margin: "0 0 2px", fontFamily: FONT_BODY,
                          }}>{count}</p>
                          <p style={{
                            fontSize: "10px", color: "#5a5a6e", margin: 0,
                            fontFamily: FONT_BODY,
                          }}>{pos}</p>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}

          {/* --- Teams Tab --- */}
          {tab === "teams" && (
            <div style={{ maxHeight: "400px", overflowY: "auto" }}>
              {[...teams]
                .sort((a, b) => (a.username === username ? -1 : b.username === username ? 1 : 0))
                .map((team) => (
                  <TeamCard key={team.username} team={team} isYou={team.username === username} />
                ))}
            </div>
          )}

          {/* --- All Picks / History Tab --- */}
          {tab === "allPicks" && (
            <>
              {allPicks.length === 0 ? (
                <div style={{
                  textAlign: "center", padding: "40px 0",
                  color: "#5a5a6e", fontSize: "13px",
                  fontFamily: FONT_BODY,
                }}>No picks have been made yet.</div>
              ) : (
                <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                  {[...allPicks].reverse().map((rawPick, i) => {
                    const info = pickedInfo[rawPick.playerId] || {};
                    const pick = { ...rawPick, name: rawPick.name || info.name, position: rawPick.position || info.position };
                    const c = getPositionColor(pick.position);
                    const isYou = pick.draftedBy === username;
                    return (
                      <div key={i} style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        padding: "12px 14px",
                        background: isYou ? "rgba(201, 162, 39, 0.04)" : "rgba(255,255,255,0.02)",
                        border: `1px solid ${isYou ? "rgba(201, 162, 39, 0.12)" : "rgba(255,255,255,0.04)"}`,
                        borderRadius: "8px", marginBottom: "6px",
                      }}>
                        <span style={{
                          fontSize: "14px", fontWeight: 700, color: "#3a3a4e",
                          fontFamily: FONT_BODY,
                          minWidth: "28px",
                        }}>#{pick.overallPick}</span>
                        <span style={{
                          display: "inline-block", padding: "3px 6px",
                          fontSize: "9px", fontWeight: 700,
                          fontFamily: FONT_BODY,
                          color: c.text, background: c.bg,
                          border: `1px solid ${c.border}`, borderRadius: "3px",
                        }}>{pick.position}</span>
                        <div style={{ flex: 1 }}>
                          <p style={{
                            fontSize: "13px", fontWeight: 600, color: "#e8e6df",
                            margin: 0, fontFamily: FONT_BODY,
                          }}>{pick.name}</p>
                        </div>
                        <span style={{
                          fontSize: "11px",
                          color: isYou ? "#c9a227" : "#5a5a6e",
                          fontWeight: isYou ? 700 : 400,
                          fontFamily: FONT_BODY,
                        }}>{isYou ? "You" : pick.draftedBy}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: "12px 16px", marginBottom: "12px",
            background: error.includes("sample") ? "rgba(74, 158, 255, 0.1)" : "rgba(220, 50, 50, 0.1)",
            border: `1px solid ${error.includes("sample") ? "rgba(74, 158, 255, 0.25)" : "rgba(220, 50, 50, 0.25)"}`,
            borderRadius: "8px",
            color: error.includes("sample") ? "#4a9eff" : "#e85555",
            fontSize: "13px", fontFamily: FONT_BODY,
          }}>{error}</div>
        )}

        {!isSpectator && <div style={{ marginTop: "16px" }}><NotificationsToggle /></div>}

        {/* Footer */}
        <p style={{
          textAlign: "center", marginTop: "8px",
          fontSize: "11px", color: "#3a3a4e",
          letterSpacing: "1px", fontFamily: FONT_BODY,
        }}>
          Draft smart. Play bold. Win big.
        </p>
      </div>

      <style>{`
        * { box-sizing: border-box; }
        input::placeholder { color: #3a3a4e; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(201, 162, 39, 0.2); border-radius: 4px; }
      `}</style>
    </div>
  );
}

// --- Mock data generator for testing ---
function generateMockPlayers() {
  const teams = [
    { name: "Kansas City Chiefs", id: "1" },
    { name: "Baltimore Ravens", id: "2" },
    { name: "Buffalo Bills", id: "3" },
    { name: "Miami Dolphins", id: "4" },
  ];
  const positions = ["QB", "RB", "RB", "WR", "WR", "WR", "TE", "K"];
  const firstNames = ["Patrick", "Lamar", "Josh", "Tua", "Travis", "Derrick", "Tyreek", "Stefon", "Mark", "Isiah", "Dalton", "Zay", "George", "Rashod", "Jaylen", "Tyler"];
  const lastNames = ["Mahomes", "Jackson", "Allen", "Tagovailoa", "Kelce", "Henry", "Hill", "Diggs", "Andrews", "Pacheco", "Kincaid", "Flowers", "Kittle", "Bateman", "Waddle", "Bass"];

  const players = [];
  let id = 1000;

  teams.forEach((team) => {
    positions.forEach((pos, i) => {
      const fIdx = Math.floor(Math.random() * firstNames.length);
      const lIdx = Math.floor(Math.random() * lastNames.length);
      players.push({
        player_id: String(id++),
        name: `${firstNames[fIdx]} ${lastNames[lIdx]}`,
        position: pos,
        team: team.name,
        team_id: team.id,
      });
    });
  });

  return players;
}
